import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Camera, Aperture, Lightbulb, Layers, Sparkles, Loader2, Save, Bookmark, Trash2,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/1595241/pexels-photo-1595241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LENS_OPTIONS = ["Wide Angle", "35mm", "50mm", "85mm", "105mm", "Telephoto"];
const LIGHTING_OPTIONS = ["Natural Light", "One Speedlight", "Multiple Speedlights", "Continuous LED", "Studio Strobe"];
const LIGHTS_OPTIONS = ["1", "2", "3", "4+"];

function DropField({ label, icon: Icon, value, onChange, options, testid }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 mb-3">
        <Icon className="h-3.5 w-3.5 text-amber-glow" strokeWidth={2} /> {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-12 bg-[#121212] border-white/10 rounded-sm text-left data-[placeholder]:text-neutral-500 focus:ring-amber-glow">
          <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="bg-[#121212] border-white/10 rounded-sm">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${testid}-option-${o.replace(/\s|\+/g, "-")}`} className="focus:bg-white/10 cursor-pointer">
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const stagger = (i) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
});

function Block({ children, className = "", i = 0, testid }) {
  return (
    <motion.div
      {...stagger(i)}
      data-testid={testid}
      className={`bg-[#121212] border border-white/5 rounded-sm p-6 hover:-translate-y-1 transition-transform duration-300 ${className}`}
    >
      {children}
    </motion.div>
  );
}

function Results({ data }) {
  const r = data.result || {};
  const cs = r.camera_settings || {};
  return (
    <div className="mt-12" data-testid="suggestion-results">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="h-5 w-5 text-amber-glow" strokeWidth={1.5} />
        <h2 className="font-display text-3xl">Your Shoot Recipe</h2>
      </div>

      {r.summary && (
        <Block i={0} className="mb-4" testid="suggestion-summary">
          <p className="text-lg leading-relaxed text-neutral-200">{r.summary}</p>
          {r.scene && <p className="mt-3 text-sm text-amber-glow tracking-wide uppercase">Best for: {r.scene}</p>}
        </Block>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Block i={1} className="md:col-span-1" testid="block-camera">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="h-4 w-4 text-amber-glow" strokeWidth={1.5} />
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-300">Camera Settings</h3>
          </div>
          <dl className="space-y-3">
            {Object.entries(cs).map(([k, v]) => (
              <div key={k} className="flex justify-between items-baseline border-b border-white/5 pb-2">
                <dt className="text-sm text-neutral-500 capitalize">{k.replace(/_/g, " ")}</dt>
                <dd className="font-display text-lg text-white">{v}</dd>
              </div>
            ))}
          </dl>
        </Block>

        <Block i={2} className="md:col-span-2" testid="block-lighting">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-4 w-4 text-amber-glow" strokeWidth={1.5} />
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-300">Lighting Setup</h3>
          </div>
          <ol className="space-y-3">
            {(r.lighting_setup || []).map((step, idx) => (
              <li key={idx} className="flex gap-4 text-neutral-200">
                <span className="font-display text-amber-glow text-lg leading-none w-6 shrink-0">{idx + 1}</span>
                <span className="text-sm leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </Block>

        <Block i={3} className="md:col-span-2" testid="block-composition">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="h-4 w-4 text-amber-glow" strokeWidth={1.5} />
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-300">Composition Tips</h3>
          </div>
          <ul className="space-y-2.5">
            {(r.composition_tips || []).map((t, idx) => (
              <li key={idx} className="text-sm text-neutral-200 flex gap-3">
                <span className="text-amber-glow">—</span> {t}
              </li>
            ))}
          </ul>
        </Block>

        <Block i={4} className="md:col-span-1" testid="block-creative">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-amber-glow" strokeWidth={1.5} />
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-300">Creative Ideas</h3>
          </div>
          <ul className="space-y-2.5">
            {(r.creative_ideas || []).map((t, idx) => (
              <li key={idx} className="text-sm text-neutral-300">{t}</li>
            ))}
          </ul>
        </Block>
      </div>
    </div>
  );
}

export default function Home() {
  const [lens, setLens] = useState("");
  const [lighting, setLighting] = useState("");
  const [lights, setLights] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [presets, setPresets] = useState([]);

  const loadPresets = () => api.get("/presets").then((r) => setPresets(r.data)).catch(() => {});
  useEffect(() => { loadPresets(); }, []);

  const ready = lens && lighting && lights;

  const generate = async () => {
    if (!ready) {
      toast.error("Select lens, lighting and number of lights first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/suggestions/generate", {
        lens_type: lens, lighting_type: lighting, num_lights: lights,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const savePreset = async () => {
    if (!ready) { toast.error("Pick all three options to save a preset."); return; }
    try {
      await api.post("/presets", {
        name: `${lens} · ${lighting}`,
        lens_type: lens, lighting_type: lighting, num_lights: lights,
      });
      toast.success("Preset saved");
      loadPresets();
    } catch (e) {
      toast.error("Could not save preset");
    }
  };

  const applyPreset = (p) => {
    setLens(p.lens_type); setLighting(p.lighting_type); setLights(p.num_lights);
    toast.success(`Loaded "${p.name}"`);
  };

  const deletePreset = async (id) => {
    await api.delete(`/presets/${id}`);
    loadPresets();
  };

  return (
    <div className="min-h-screen pt-16 grain">
      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_IMG} alt="camera" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-amber-glow mb-4">AI Lighting & Lens Assistant</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-[1.05]">
            Engineer the perfect frame
          </h1>
          <p className="text-neutral-300 mt-5 max-w-xl mx-auto text-base">
            Choose your glass and your lights. Aevolux generates exact camera settings, a lighting diagram and creative direction.
          </p>
        </div>
      </div>

      {/* Generator control room */}
      <div className="relative max-w-7xl mx-auto px-6 -mt-6 pb-24">
        <div className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-sm shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-6 sm:p-8">
          <div className="grid md:grid-cols-3 gap-4">
            <DropField label="Lens Type" icon={Aperture} value={lens} onChange={setLens} options={LENS_OPTIONS} testid="lens-type-select" />
            <DropField label="Lighting Type" icon={Lightbulb} value={lighting} onChange={setLighting} options={LIGHTING_OPTIONS} testid="lighting-type-select" />
            <DropField label="Number of Lights" icon={Layers} value={lights} onChange={setLights} options={LIGHTS_OPTIONS} testid="num-lights-select" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              data-testid="generate-suggestions-button"
              onClick={generate} disabled={loading}
              className="flex-1 h-13 py-3.5 bg-white text-black font-semibold hover:bg-neutral-200 rounded-sm text-base"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Composing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Suggestions</>}
            </Button>
            <Button
              data-testid="save-preset-button"
              onClick={savePreset} variant="outline"
              className="h-13 py-3.5 bg-transparent border-white/20 text-white hover:bg-white/5 rounded-sm"
            >
              <Save className="h-4 w-4 mr-2" /> Save Preset
            </Button>
          </div>

          {presets.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 mb-3">
                <Bookmark className="h-3.5 w-3.5 text-amber-glow" /> Your Presets
              </p>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <div key={p._id} data-testid="preset-chip" className="group flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-sm pl-3 pr-1 py-1.5">
                    <button onClick={() => applyPreset(p)} className="text-sm text-neutral-200 hover:text-amber-glow transition-colors">
                      {p.name} · {p.num_lights} 💡
                    </button>
                    <button data-testid="delete-preset-button" onClick={() => deletePreset(p._id)} className="text-neutral-600 hover:text-red-400 p-1">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="mt-12 flex flex-col items-center justify-center py-16 text-neutral-400">
            <Loader2 className="h-8 w-8 animate-spin text-amber-glow mb-4" />
            <p className="text-sm tracking-wide">Claude is calculating your exposure & lighting…</p>
          </div>
        )}

        {result && !loading && <Results data={result} />}
      </div>

      <footer className="border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-neutral-500">
          <span>© {new Date().getFullYear()} Chapter 8 Studio</span>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-amber-glow transition-colors">Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}
